import * as React from 'react';
import type LocationCommonWellItemViewState from 'owa-location-common-well/lib/store/schema/LocationCommonWellItemViewState';
import { usePatchPayload, useProjectId, useAssetId } from 'owa-timestream-asset-sdk';
import type { WeatherPayload, WeatherLocationPickerProps } from '../schema';
import { focusAsset } from 'owa-timestream-asset-sdk-actions';

export default function useWeatherLocationPickerViewState({
    selectedLocation,
    locationName,
    isLoading,
}: WeatherLocationPickerProps) {
    const [locationPersona, setLocationPersona] = React.useState<
        LocationCommonWellItemViewState | undefined
    >(selectedLocation);
    const [isPickerVisible, setIsPickerVisible] = React.useState<boolean>(!locationName);
    const pickerRef = React.useRef<HTMLDivElement | null>(null);
    const patchPayload = usePatchPayload<WeatherPayload>();
    const projectId = useProjectId();
    const assetId = useAssetId();

    const onLocationSelected = React.useCallback(
        (items: LocationCommonWellItemViewState[]) => {
            const item = items?.length > 0 ? items[0] : undefined;
            setLocationPersona(item);
            patchPayload({ selectedLocation: item });
            focusAsset(projectId, assetId);
        },
        [projectId, assetId]
    );
    const onLocationRemoved = React.useCallback(() => {
        setLocationPersona(undefined);
        patchPayload({ selectedLocation: undefined });
    }, []);

    const showLocationPicker = React.useCallback(() => setIsPickerVisible(true), []);
    const handleOutsideClick = React.useCallback(
        (event: MouseEvent) => {
            if (!pickerRef?.current?.contains(event.target as Node) && !!locationName) {
                setIsPickerVisible(false);
            }
        },
        [pickerRef.current, locationName]
    );

    React.useEffect(() => {
        document.addEventListener('click', handleOutsideClick);
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [pickerRef.current, locationName]);

    React.useEffect(() => {
        !!locationName && setIsPickerVisible(false);
    }, [locationName]);

    return {
        selectedLocation,
        locationName,
        locationPersona,
        pickerRef,
        isPickerVisible,
        findControlProps: {
            onItemSelected: onLocationSelected,
        },
        locationItemProps: {
            removeOperation: onLocationRemoved,
            expandGroup: () => {}, // not applicable in this case
            onRenderFBBadge: () => <div />, // not applicable in this case
        },
        titleProps: {
            tabIndex: 0,
            onClick: showLocationPicker,
            onFocus: showLocationPicker,
            title: locationName,
        },
        isLoading,
    };
}
